import { Code2, History, Power, Upload } from 'lucide-react';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { useActiveProject } from '../../shared/api/useActiveProject.js';
import { useReleases, useScriptActions, useScriptStatus } from '../../shared/api/queries.js';
import { IntegrationTag } from './IntegrationTag.js';
import { ReleaseTable } from './ReleaseTable.js';
import { SuspensionSwitch } from './SuspensionSwitch.js';
import './script.css';

/**
 * "Script" screen (functional §5.7, §6.2): the tag with its integrity, the
 * suspension switch and the configuration versions. Central access only; the
 * API refuses anything else regardless of what the menu shows.
 */
export function ScriptPage() {
  const { project } = useActiveProject();
  const status = useScriptStatus(project?.id);
  const releases = useReleases(project?.id);
  const actions = useScriptActions(project?.id);

  return (
    <>
      <PageHeader
        title="Administration du script"
        description="Ce qui fait fonctionner l'aide sur le portail : la ligne à poser dans le site, l'interrupteur qui coupe tout et l'historique des versions mises en ligne."
        actions={
          <Button
            variant="primary"
            icon={<Upload size={14} />}
            disabled={!project || actions.publish.isPending}
            onClick={() => actions.publish.mutate()}
            title="Regroupe les aides publiées dans une nouvelle version. Le portail la reçoit dans la minute."
          >
            {actions.publish.isPending ? 'Mise en ligne…' : 'Mettre en ligne une version'}
          </Button>
        }
      />

      {status.data && (
        <Card title="Interrupteur général" icon={<Power size={15} />} className="script__card">
          <SuspensionSwitch
            enabled={status.data.enabled}
            busy={actions.suspend.isPending || actions.resume.isPending}
            onSuspend={() => actions.suspend.mutate()}
            onResume={() => actions.resume.mutate()}
          />
        </Card>
      )}

      {status.data && project && (
        <Card title="Ligne à poser dans le portail" icon={<Code2 size={15} />} className="script__card">
          <IntegrationTag status={status.data} />
        </Card>
      )}

      <Card
        title="Versions mises en ligne"
        icon={<History size={15} />}
        padded={false}
        className="script__card"
      >
        <p className="script__hint script__hint--padded">
          Chaque mise en ligne crée une version qui regroupe les aides publiées. En cas de problème, remettez
          une version précédente en ligne : le portail la reçoit dans la minute.
        </p>
        <ReleaseTable
          releases={releases.data ?? []}
          latestVersion={status.data?.latestVersion ?? 0}
          busy={actions.activate.isPending}
          onActivate={(version) => actions.activate.mutate(version)}
        />
      </Card>
    </>
  );
}
